import { useMemo } from 'react';

import { ResourceTypeExplorer } from '@dbt-labs/dbt-dag';
import {
  Link,
  Ryecon,
  RyeconFreshnessError,
  RyeconFreshnessPassed,
  RyeconFreshnessStale,
  RyeconFreshnessUnknown,
  RyeconHelp,
  RyeconStatusError,
  RyeconStatusReused,
  RyeconStatusSkipped,
  RyeconStatusSuccess,
  RyeconStatusWarning,
} from '@dbt-labs/sourdough';

import {
  FreshnessStatus,
  SourceAppliedStateNestedNode,
  SourceAppliedStateNode,
  TestAppliedStateNestedNode,
} from '@/__generated__/graphql';
import { DetailsSection } from '@/components/SectionWithCard';
import { TimestampDisplay } from '@/components/Timestamp';
import { RequestedModelAppliedNode } from '@/hooks/discovery/model';
import { RequestedSeedAppliedNode } from '@/hooks/discovery/seed';
import { RequestedSnapshotAppliedNode } from '@/hooks/discovery/snapshot';
import { formatAbsoluteLocalDate, formatDateCommon } from '@/util/date';
import {
  computeSourceFreshness,
  isFreshnessCheckConfigured,
  STALE_FRESHNESS_THRESHOLD_IN_DAYS,
} from '@/util/dbt/freshness';
import {
  ResourceStatus,
  ResourceStatusResult,
  ResourceStatusSection,
} from './ResourceStatusSection';
import { TestResultsSection } from './TestResultsSection';
import { UpstreamSourcesSection } from './UpstreamSourcesSection';

type LatestStatusSectionDisplayParams = {
  resourceType: ResourceTypeExplorer;
  header: string;
  lastRunId?: number | null;
  checkCompletedAt?: string;
  checkCompletedAtUtc?: string;
  tests: TestAppliedStateNestedNode[] | undefined;
  upstreamSources?: SourceAppliedStateNestedNode[] | undefined;
  status: ResourceStatusResult;
  statusIcon: Ryecon;
  tooltip: React.ReactNode;
};

const LatestStatusSectionDisplay = ({
  resourceType,
  header,
  lastRunId,
  checkCompletedAt,
  checkCompletedAtUtc,
  tests,
  upstreamSources,
  status,
  statusIcon,
  tooltip,
}: LatestStatusSectionDisplayParams) => (
  <DetailsSection
    heading="Latest status"
    withCard={false}
    withHeading={
      <TimestampDisplay
        timestamp={checkCompletedAt}
        timestampUtc={checkCompletedAtUtc}
        prependedText="as of "
      />
    }
  >
    <div className="overflow-hidden rounded-md border border-borderMuted">
      <ResourceStatusSection
        header={header}
        lastRunId={lastRunId || null}
        shouldIndent={tests ? tests.length > 0 : false}
        status={status}
        statusIcon={statusIcon}
        tooltip={tooltip}
      />
      <TestResultsSection resourceType={resourceType} tests={tests} />
      <UpstreamSourcesSection sources={upstreamSources} />
    </div>
  </DetailsSection>
);

const lastRunStatusCriteriaFn = (runStatus: ResourceStatus) => {
  switch (runStatus) {
    case 'error':
      return ResourceStatusResult.error;
    case 'success':
      return ResourceStatusResult.pass;
    case 'skipped':
      return ResourceStatusResult.warn;
    case 'reused':
      return ResourceStatusResult.reused;
    default:
      return ResourceStatusResult.unknown;
  }
};

const lastRunStatusTooltipCriteriaFn = (
  runStatus: ResourceStatus,
  resourceType: ResourceTypeExplorer,
): React.ReactNode => {
  switch (runStatus) {
    case 'error':
      return `This ${resourceType} was not built in its last run due to an error`;
    case 'success':
      return `This ${resourceType} was successfully built in its last run`;
    case 'skipped':
      return `This ${resourceType} was not built in its last run due to it being skipped`;
    case 'reused':
      return (
        <span className="text-sm">
          This {resourceType} was reused instead of being rebuilt because of State Aware
          Orchestration.{' '}
          <Link
            isInternal={false}
            to="https://docs.getdbt.com/docs/deploy/state-aware-about"
          >
            Learn more here.
          </Link>
        </span>
      );
    default:
      return `The latest run status of this ${resourceType} could not be determined`;
  }
};

export const runStatusResultRyeconMap: Record<ResourceStatusResult, Ryecon> = {
  0: RyeconHelp,
  1: RyeconStatusSuccess,
  2: RyeconStatusWarning,
  3: RyeconStatusError,
  4: RyeconHelp,
  5: RyeconStatusSkipped,
  6: RyeconStatusReused,
};

export type LatestRunStatusSectionParams = {
  resourceType: ResourceTypeExplorer;
  applied:
    | RequestedModelAppliedNode
    | RequestedSeedAppliedNode
    | RequestedSnapshotAppliedNode
    | null;
};

export const LatestRunStatusSection = ({
  resourceType,
  applied,
}: LatestRunStatusSectionParams) => {
  const resourceStatus = applied?.executionInfo?.lastRunStatus ?? 'unknown';
  const status = lastRunStatusCriteriaFn(resourceStatus);
  const statusIcon = runStatusResultRyeconMap[status];

  const date = applied?.executionInfo?.executeCompletedAt
    ? new Date(applied.executionInfo.executeCompletedAt)
    : undefined;
  const checkCompletedAt = date && formatAbsoluteLocalDate(date);
  const checkCompletedAtUtc = date && formatDateCommon(date).utc;

  const upstreamSources: SourceAppliedStateNestedNode[] | undefined = useMemo(() => {
    if (!applied || !('ancestors' in applied)) {
      return undefined;
    }
    return applied.ancestors?.filter(
      (ancestor) => ancestor.resourceType === 'source',
    ) as SourceAppliedStateNestedNode[];
  }, [applied]);
  return (
    <LatestStatusSectionDisplay
      resourceType={resourceType}
      header="Last run status"
      lastRunId={applied?.executionInfo?.lastRunId}
      checkCompletedAt={checkCompletedAt}
      checkCompletedAtUtc={checkCompletedAtUtc}
      upstreamSources={upstreamSources}
      tests={applied?.tests as TestAppliedStateNestedNode[]}
      status={status}
      statusIcon={statusIcon}
      tooltip={lastRunStatusTooltipCriteriaFn(resourceStatus, resourceType)}
    />
  );
};

const freshnessStatusCriteriaFn = (runStatus: ResourceStatus) => {
  if (!runStatus) {
    return ResourceStatusResult.stale;
  }
  switch (runStatus?.toLowerCase()) {
    case 'error':
    case 'runtime error':
      return ResourceStatusResult.error;
    case 'pass':
      return ResourceStatusResult.pass;
    case 'warn':
      return ResourceStatusResult.warn;
    case 'skipped':
      return ResourceStatusResult.skipped;
    case 'outdated':
    case 'unconfigured':
      return ResourceStatusResult.warn;
    default:
      return ResourceStatusResult.unknown;
  }
};

const freshnessHeaderCriteriaFn = (runStatus: ResourceStatus) => {
  switch (runStatus?.toLowerCase()) {
    case 'error':
    case 'runtime error':
      return 'Data is stale';
    case 'pass':
      return 'Data is fresh';
    case 'warn':
      return 'Data may be stale';
    case 'skipped':
      return 'Data freshness skipped';
    case 'outdated':
      return 'Data freshness not run recently';
    case 'unconfigured':
      return 'Data freshness unconfigured';
    default:
      return 'Data freshness unknown';
  }
};

const freshnessTooltipCriteriaFn = (runStatus: ResourceStatus): React.ReactNode => {
  const staleTooltip = `This source has not had its freshness checked in over ${STALE_FRESHNESS_THRESHOLD_IN_DAYS} days`;
  if (!runStatus) {
    return staleTooltip;
  }
  switch (runStatus?.toLowerCase()) {
    case 'outdated':
      return "Freshness hasn't run recently";
    case 'error':
      return 'Freshness check failed for this source.';
    case 'runtime error':
      return "This source's freshness check was unable to run";
    case 'pass':
      return 'This source passed its freshness check';
    case 'warn':
      return 'This source received a warning from its freshness check';
    case 'skipped':
      return 'This source has been opted out of freshness checks.';
    case 'unconfigured':
    default:
      return 'This source does not have a freshness check configured';
  }
};

export const freshnessStatusResultRyeconMap: Record<ResourceStatusResult, Ryecon> = {
  0: RyeconFreshnessUnknown,
  1: RyeconFreshnessPassed,
  2: RyeconFreshnessStale,
  3: RyeconFreshnessError,
  4: RyeconFreshnessUnknown,
  5: RyeconStatusSkipped,
  6: RyeconStatusReused,
};

export type LatestFreshnessStatusSectionParams = {
  resourceType: ResourceTypeExplorer;
  applied: SourceAppliedStateNode | undefined;
  checkCompletedAt?: string;
  checkCompletedAtUtc?: string;
};

export const LatestFreshnessStatusSection = ({
  resourceType,
  applied,
  checkCompletedAt,
  checkCompletedAtUtc,
}: LatestFreshnessStatusSectionParams) => {
  const freshness = applied?.freshness ?? null;
  const resourceStatus =
    freshness?.freshnessStatus === FreshnessStatus.Skipped
      ? FreshnessStatus.Skipped
      : isFreshnessCheckConfigured(freshness)
        ? computeSourceFreshness(applied?.freshness ?? null)
        : FreshnessStatus.Unknown;
  const status = freshnessStatusCriteriaFn(resourceStatus);
  const statusIcon = freshnessStatusResultRyeconMap[status];
  return (
    <LatestStatusSectionDisplay
      resourceType={resourceType}
      header={freshnessHeaderCriteriaFn(resourceStatus)}
      lastRunId={applied?.freshness?.freshnessRunId}
      checkCompletedAt={checkCompletedAt}
      checkCompletedAtUtc={checkCompletedAtUtc}
      tests={applied?.tests as TestAppliedStateNestedNode[]}
      status={status}
      statusIcon={statusIcon}
      tooltip={freshnessTooltipCriteriaFn(resourceStatus)}
    />
  );
};
